import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import values from 'lodash/values';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  stringSelectSpec,
  boolCheckboxSpec,
  stringInputSpec,
} from '../../lib/editing-configs';

import BackgroundColor from '../background-color/background-color';
import './vertical-spacer.less';

const spacerMap = {
  'p-8': 'p-8',
  'p-12': 'p-12',
  'p-18': 'p-18',
  'p-24': 'p-24',
  'p-30': 'p-30',
  'p-40': 'p-40',
  'p-60': 'p-60',
};

const spacerMapCurrency = {
  'margin-1': 'margin-1',
  'margin-2': 'margin-2',
  'margin-3': 'margin-3',
  'margin-3-half': 'margin-3-half',
  'margin-4': 'margin-4',
  'margin-5': 'margin-5',
};

const backgroundSizes = [
  { label: 'Contain', value: 'contain' },
  { label: 'Cover', value: 'cover' },
  { label: 'Auto', value: 'auto' },
];

const VerticalSpacer = ({
  desktop,
  tablet,
  mobile,
  bgOptions,
  backgroundColor: obsoleteBackgroundColor,
  useCurrencyMargins,
  backgroundImageDesktop,
  backgroundImageTablet,
  backgroundImageMobile,
  backgroundSize,
  size,
}) => {
  function getBackgroundColorConfig() {
    // For backwards compatibility!
    if (obsoleteBackgroundColor && !bgOptions?.backgroundColor) {
      return { backgroundColor: obsoleteBackgroundColor };
    }
    return bgOptions;
  }

  const getBackgroundImageSrcMap = useCallback(
    (bgSize) => {
      const type = {
        lg: backgroundImageDesktop,
        md: backgroundImageTablet,
        sm: backgroundImageMobile,
      };
      if (bgSize !== 'lg' && !type[bgSize]) {
        return type.lg;
      }
      return type[bgSize];
    },
    [backgroundImageDesktop, backgroundImageMobile, backgroundImageTablet]
  );

  function getMarginStylesByVersion(viewport) {
    if (useCurrencyMargins) {
      return `${spacerMapCurrency[viewport]}`;
    }
    return `${spacerMap[viewport]}`;
  }

  return (
    <BackgroundColor bgOptions={getBackgroundColorConfig()}>
      {(backgroundColor) => (
        <div
          className={classNames({
            [`${getMarginStylesByVersion(desktop)}-d`]: desktop,
            [`${getMarginStylesByVersion(tablet)}-t`]: tablet,
            [`${getMarginStylesByVersion(mobile)}-m`]: mobile,
          })}
          style={{
            backgroundColor,
            backgroundImage: `url(${getBackgroundImageSrcMap(size)})`,
            backgroundSize,
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}
    </BackgroundColor>
  );
};

VerticalSpacer.propTypes = {
  ...BackgroundColor.propTypes,
  desktop: PropTypes.oneOf([
    ...values(spacerMap).concat(values(spacerMapCurrency)),
  ]),
  tablet: PropTypes.oneOf([
    ...values(spacerMap).concat(values(spacerMapCurrency)),
  ]),
  mobile: PropTypes.oneOf([
    ...values(spacerMap).concat(values(spacerMapCurrency)),
  ]),
  useCurrencyMargins: PropTypes.bool,
  children: PropTypes.node,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
};

VerticalSpacer.defaultProps = {
  useCurrencyMargins: false,
  children: null,
  backgroundSize: 'auto',
};

function marginTypeConfig(version) {
  const spacerOptions = [{ label: 'Select one', value: '' }].concat(
    Object.keys(version).map((key) => ({
      label: key,
      value: version[key],
    }))
  );

  return [
    stringSelectSpec('mobile', 'Mobile', spacerOptions, {
      help: 'Mobile height',
    }),
    stringSelectSpec('tablet', 'Tablet', spacerOptions, {
      help: 'Tablet height',
    }),
    stringSelectSpec('desktop', 'Desktop', spacerOptions, {
      help: 'Desktop height',
    }),
  ];
}

VerticalSpacer.getEditingConfig = (componentDocument) => {
  const marginTypeOutput =
    get(componentDocument, 'props.useCurrencyMargins', false) === true
      ? marginTypeConfig(spacerMapCurrency)
      : marginTypeConfig(spacerMap);

  return [
    ...BackgroundColor.getEditingConfig(componentDocument),
    stringInputSpec('backgroundImageDesktop', 'Background Image URL (Desktop)'),
    stringInputSpec('backgroundImageTablet', 'Background Image URL (Tablet)'),
    stringInputSpec('backgroundImageMobile', 'Background Image URL (Mobile)'),
    stringSelectSpec('backgroundSize', 'Background Size', backgroundSizes),
    boolCheckboxSpec('useCurrencyMargins', 'Use Currency margins?'),
    ...marginTypeOutput,
  ];
};

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(VerticalSpacer);
